import React, { useState, useContext, useMemo, useEffect, useRef, useCallback, memo } from 'react';
import { 
  Box, Typography, Button, IconButton, Collapse, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import Grid from '@mui/material/Grid'; 
import { alpha } from '@mui/material/styles'; 
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'; 
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'; 
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'; 
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewListIcon from '@mui/icons-material/ViewList';
import FullscreenIcon from '@mui/icons-material/Fullscreen'; 
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // 👇 Icon cho helpText

import AuthContext from "./auth/Context"; 
import BaseFieldControl from './BaseFieldControl';
import { formatDisplayValue } from './DataListItems';
import { fieldCellSx } from './fieldFormat';
import { areRowPropsEqual, gridDependsOnMaster } from './detailRowMemo';
import { buildValueChangedArgs, masterPatchFrom } from './lifecycleHooks';
import { coerceFieldValue } from './formRules';
import * as utils from '../utils';
import Moment from 'moment';
import Numeral from 'numeral';
//import DynamicValueControl from './DynamicValueControl'; 
import Dialog from "./ManagedModal";

// =====================================================================
// HÀM TIỆN ÍCH
// =====================================================================
const getNestedValue = (obj, path) => {
  if (!path || !obj) return undefined;
  return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined) ? acc[part] : undefined, obj);
};

const setNestedValue = (obj, path, value) => {
  if (!path) return obj;
  const parts = path.split('.');
  if (parts.length === 1) return { ...obj, [path]: value };
  const result = { ...obj };
  let current = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    current[part] = { ...(current[part] || {}) };
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;
  return result;
};

// 👇 THAY ĐỔI: Thêm Code, Object, Mixed vào danh sách mở Modal
const LARGE_FIELD_TYPES = ['RichText', 'Dashboard', 'ObjectArray', 'FileArray', 'Code', 'Object', 'Mixed']; 

// =====================================================================
// MỘT DÒNG CỦA LƯỚI CHI TIẾT — CÓ memo
// -----------------------------------------------------------------------------
// Vì sao cần: `masterData` (toàn bộ chứng từ) đổi tham chiếu mỗi lần gõ phím ở BẤT KỲ ô nào, kể cả ô
// trên phần master. Không có memo thì mọi ô của mọi dòng render lại theo — với 30 dòng × 3 cột
// autocomplete là ~90 ô, mỗi ô lại kéo theo effect + JSON.stringify + setState. Bộ cũ (InputBase)
// chặn đúng chỗ này bằng `shouldComponentUpdate` của AutoCompleteInput.
//
// GIẢM TẢI: chỉ dòng ĐANG SỬA mới mount control thật; các dòng còn lại render CHỮ (giống bộ cũ khi
// `col_editable` tắt). Bấm/tab vào ô nào thì dòng đó "bật" và con trỏ nhảy đúng ô đó.
// =====================================================================
const DetailTableRow = memo(function DetailTableRow({
  idx, item, visibleColumns, subFields, cellFields, isReadOnly, isActive, focusField,
  rowErrors, masterData, dependsOnMaster, context, isLast,
  isHiddenCell, isCellReadOnly, isLargeType, onActivate, onCellChange, onOpenCellModal, renderActions
}) {
  const rowRef = useRef(null);

  // Bật dòng xong thì đưa con trỏ vào đúng ô vừa bấm (control chỉ vừa được mount ở lượt render này).
  useEffect(() => {
    if (!isActive || !focusField || !rowRef.current) return;
    const cells = rowRef.current.querySelectorAll('[data-cell]');
    for (const cell of cells) {
      if (cell.getAttribute('data-cell') !== focusField) continue;
      const el = cell.querySelector('input, textarea');
      if (el) { try { el.focus(); if (el.select) el.select(); } catch (e) { /* bỏ qua */ } }
      break;
    }
  }, [isActive, focusField]);

  const hasChildError = Object.keys(rowErrors).length > 0;
  const rowBgColor = hasChildError ? '#ffebee' : '#ffffff';

  return (
    <TableRow ref={rowRef}>
      {/* Cố định cột STT (#) Body */}
      <TableCell align="center" sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', position: 'sticky', left: 0, zIndex: 1, bgcolor: rowBgColor }}>
        {idx + 1}
      </TableCell>

      {visibleColumns.map(subF => {
        const subErr = rowErrors[subF.field];
        const isHidden = isHiddenCell(subF, item);
        // KHOÁ THEO TỪNG Ô: `subF.readOnly` (kể cả dạng script, đúng theo từng dòng) cộng thêm cờ khoá
        // của cả lưới. Trước đây chỉ có cờ cả lưới nên `readOnly` khai ở cột chi tiết không có tác dụng.
        const cellReadOnly = isReadOnly || isCellReadOnly(subF, item);
        const isLargeField = isLargeType(subF);
        const value = getNestedValue(item, subF.field);
        const hasValue = !!value;

        return (
          <TableCell data-cell={subF.field} key={subF.field} sx={{ p: 1, verticalAlign: 'top', border: '1px solid', borderColor: 'divider', bgcolor: rowBgColor }}>
            {isHidden ? (
              <Typography variant="caption" color="text.disabled" fontStyle="italic">- Ẩn -</Typography>
            ) : isLargeField ? (
              <Button fullWidth size="small" variant={hasValue ? "outlined" : "dashed"} color={subErr ? "error" : "primary"} startIcon={<FullscreenIcon />} onClick={() => onOpenCellModal(idx, subF, item)} sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 'normal', borderStyle: hasValue ? 'solid' : 'dashed', bgcolor: 'background.paper' }}>
                {hasValue ? 'Đã thiết lập' : (cellReadOnly ? 'Chưa có' : 'Mở rộng')}
              </Button>
            ) : isActive ? (
              <BaseFieldControl
                field={cellFields[subF.field] || subF}
                value={value}
                onChange={(newVal, optionObj) => onCellChange(idx, subF, newVal, optionObj)}
                error={subErr} isReadOnly={cellReadOnly} formData={item} masterData={masterData} formFields={subFields}
              />
            ) : (
              <Box
                tabIndex={cellReadOnly ? -1 : 0}
                onMouseDown={cellReadOnly ? undefined : () => onActivate(idx, subF.field)}
                onFocus={cellReadOnly ? undefined : () => onActivate(idx, subF.field)}
                sx={{
                  minHeight: 32, px: 1, py: 0.75, borderRadius: 1, fontSize: '0.875rem',
                  display: 'flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                  ...fieldCellSx(subF),
                  // canh phải/giữa của cột số phải tác dụng lên flex, không chỉ text
                  justifyContent: (subF.align || (subF.type === 'Number' ? 'right' : 'left')) === 'right' ? 'flex-end'
                    : (subF.align === 'center' ? 'center' : 'flex-start'),
                  cursor: cellReadOnly ? 'default' : 'text',
                  border: '1px solid', borderColor: subErr ? 'error.main' : 'transparent',
                  color: value === undefined || value === null || value === '' ? 'text.disabled' : 'text.primary',
                  '&:hover': cellReadOnly ? undefined : { borderColor: 'divider', bgcolor: 'action.hover' },
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                }}
              >
                {formatDisplayValue(value, subF, item, context, false)}
              </Box>
            )}
          </TableCell>
        );
      })}

      {!isReadOnly && (
        <TableCell align="center" sx={{ verticalAlign: 'middle', border: '1px solid', borderColor: 'divider', position: 'sticky', right: 0, zIndex: 1, bgcolor: rowBgColor }}>
          {renderActions(idx, isLast)}
        </TableCell>
      )}
    </TableRow>
  );
}, areRowPropsEqual);

export default function ObjectArrayControl({
  field, value, onChange, formErrors, isReadOnly,
  isMobile, masterData,
  fillHeight // true (bố cục chứng từ): control lấp đầy chiều cao, CHỈ vùng dòng cuộn, header table cố định
}) {
  const context = useContext(AuthContext); 
  const subItems = Array.isArray(value) ? value : [];
  // Tham chiếu ỔN ĐỊNH: `field.subFields || []` tạo mảng RỖNG MỚI mỗi lần render khi field chưa
  // khai subFields -> mọi useMemo phụ thuộc nó (cellFields, dependsOnMaster, visibleTableColumns)
  // bị tính lại -> memo của dòng vô hiệu -> render lại toàn lưới. Ghim bằng useMemo.
  const subFields = useMemo(() => field.subFields || [], [field.subFields]);
  const parentError = formErrors[field.field];

  const isPrimitiveArray = !subFields || subFields.length === 0;
  const hasFixedItemType = !!field.itemType; 
  const primitiveType = field.itemType || 'String'; 

  const [expanded, setExpanded] = useState(() => subItems.map(() => false));
  const [manualViewMode, setManualViewMode] = useState(null);
  
  const viewMode = useMemo(() => {
    if (manualViewMode) return manualViewMode; 
    if (isMobile) return 'list';               
    if (field.defaultViewMode) return field.defaultViewMode; 
    return 'table';                            
  }, [manualViewMode, isMobile, field.defaultViewMode]);

  // =====================================================================
  // HIỆU NĂNG: giữ tham chiếu MỚI NHẤT trong ref để các callback truyền xuống dòng KHÔNG đổi identity
  // (đổi identity là memo của dòng vô hiệu -> quay lại cảnh render toàn lưới mỗi lần gõ phím).
  // =====================================================================
  const subItemsRef = useRef(subItems);   subItemsRef.current = subItems;
  const masterDataRef = useRef(masterData); masterDataRef.current = masterData;
  const subFieldsRef = useRef(subFields);  subFieldsRef.current = subFields;

  // Ô đang sửa: { row, field }. Chỉ DÒNG này mount control thật, các dòng khác chỉ là chữ.
  const [activeCell, setActiveCell] = useState(null);
  const activateCell = useCallback((rowIdx, fieldName) => {
    setActiveCell(prev => (prev && prev.row === rowIdx && prev.field === fieldName) ? prev : { row: rowIdx, field: fieldName });
  }, []);

  // Bản sao field cho Ô LƯỚI (ẩn nhãn) — dựng MỘT LẦN theo subFields thay vì tạo object mới mỗi render.
  const cellFields = useMemo(() => {
    const map = {};
    for (const sf of subFields) map[sf.field] = { ...sf, label: '', helpText: '' };
    return map;
  }, [subFields]);

  // Lưới có script nào đọc dữ liệu chứng từ mẹ không? Không có thì dòng khỏi vẽ lại khi master đổi.
  const dependsOnMaster = useMemo(() => gridDependsOnMaster(subFields), [subFields]);

  // =====================================================================
  // QUẢN LÝ MODAL & BACK BUTTON
  // =====================================================================
  const [cellModalData, setCellModalData] = useState(null);

  const openCellModal = (idx, subF, item) => {
    setCellModalData({ idx, subF, item });
    window.history.pushState({ modalOpen: true }, '');
  };

  const closeCellModal = () => {
    setCellModalData(null);
    if (window.history.state?.modalOpen) {
      window.history.back(); 
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      if (cellModalData) setCellModalData(null);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [cellModalData]);

  // =====================================================================
  // HELPER: RENDER NHÃN KÈM BIỂU TƯỢNG HELP
  // =====================================================================
  const renderLabelWithHelp = (label, helpText, required) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Typography component="span" variant="inherit" fontWeight="inherit">
        {label} {required && <span style={{color:'red'}}>*</span>}
      </Typography>
      {helpText && (
        <Tooltip title={helpText} arrow placement="top">
          <HelpOutlineIcon 
            sx={{ 
              fontSize: 16, 
              color: 'text.secondary', 
              cursor: 'help',
              opacity: 0.7,
              '&:hover': { opacity: 1 }
            }} 
          />
        </Tooltip>
      )}
    </Box>
  );

  // =====================================================================

  const evaluateSubFieldCondition = (condition, rowData) => {
    if (!condition) return false;
    if (typeof condition === 'function') {
      // (context, formData=dòng con, masterData=bản ghi mẹ, fields=toàn bộ subFields)
      try { return condition(context, rowData, masterData || rowData, subFields); } catch (e) { return false; }
    }
    return !!condition;
  };

  const visibleTableColumns = useMemo(() => {
    return subFields.filter(subF => {
      if (typeof subF.hideInForm === 'function') return true; 
      return !subF.hideInForm;
    });
  }, [subFields]);

  const handleAddRow = () => {
    if (isPrimitiveArray) {
      let defaultVal = '';
      if (hasFixedItemType) {
        if (primitiveType === 'Number') defaultVal = 0;
        if (primitiveType === 'Boolean') defaultVal = false;
      }
      onChange([...subItems, defaultVal]);
      setExpanded([...expanded, false]);
    } else {
      const newItem = {};
      subFields.forEach(sub => { 
          const defaultVal = sub.default !== undefined ? sub.default : sub.defaultValue;
          newItem[sub.field] = defaultVal !== undefined ? defaultVal : ''; 
      });
      onChange([...subItems, newItem]);
      setExpanded([...expanded.map(() => false), true]);
      // Bật ngay dòng vừa thêm ở cột đầu -> gõ được luôn, không phải bấm thêm một nhát.
      const firstCol = (visibleTableColumns[0] || subFields[0] || {}).field;
      if (firstCol) setActiveCell({ row: subItems.length, field: firstCol });
    }
  };

  const handleRemoveRow = (e, indexToRemove) => {
    e.stopPropagation(); 
    onChange(subItems.filter((_, i) => i !== indexToRemove));
    setExpanded(expanded.filter((_, i) => i !== indexToRemove));
    setActiveCell(null);   // chỉ số dòng đã đổi -> bỏ đánh dấu ô đang sửa cho khỏi trỏ nhầm dòng
  };

  const handleDuplicateRow = (e, index) => {
    e.stopPropagation();
    const newItems = [...subItems];
    if (isPrimitiveArray) {
      newItems.splice(index + 1, 0, subItems[index]);
    } else {
      const itemToCopy = JSON.parse(JSON.stringify(subItems[index]));
      delete itemToCopy._id; delete itemToCopy.id;
      newItems.splice(index + 1, 0, itemToCopy);
    }
    const newExpanded = [...expanded.map(() => false)]; 
    newExpanded.splice(index + 1, 0, true); 
    onChange(newItems);
    setExpanded(newExpanded);
    setActiveCell(null);   // nhân đôi dòng -> chỉ số dịch chuyển
  };

  const handleMoveRow = (e, index, direction) => {
    e.stopPropagation();
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === subItems.length - 1) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const newItems = [...subItems];
    const tempItem = newItems[index];
    newItems[index] = newItems[targetIndex];
    newItems[targetIndex] = tempItem;
    const newExpanded = [...expanded];
    const tempExp = newExpanded[index];
    newExpanded[index] = newExpanded[targetIndex];
    newExpanded[targetIndex] = tempExp;
    onChange(newItems);
    setExpanded(newExpanded);
    setActiveCell(null);   // đổi thứ tự dòng -> bỏ đánh dấu ô đang sửa
  };

  const toggleExpand = (index) => {
    const newExpanded = [...expanded];
    newExpanded[index] = !newExpanded[index];
    setExpanded(newExpanded);
  };

  const handlePrimitiveChange = (index, newValue) => {
    const newArray = [...subItems];
    newArray[index] = newValue;
    onChange(newArray);
  };

  const handleChildChange = async (index, childFieldConfig, newValue, optionObj) => {
    // Chốt cuối: ô chỉ-đọc thì không nhận thay đổi nào. Giao diện đã khoá rồi, nhưng đường vào còn có
    // hộp thoại mở rộng và cả script gọi thẳng — khoá quyền GHI thì phải khoá ở chỗ GHI.
    if (isReadOnly || isCellReadOnly(childFieldConfig, subItems[index])) return;
    // Xem chú thích ở DataListForm.handleFieldChange: ô số trả về CHUỖI, phải ép ngay tại cửa vào.
    newValue = coerceFieldValue(childFieldConfig, newValue);
    const newArray = [...subItems];
    let updatedItem = setNestedValue(newArray[index], childFieldConfig.field, newValue);

    if ((childFieldConfig.type === 'Autocomplete' || childFieldConfig.type === 'Select') && optionObj) {
        const apiLabelMap = childFieldConfig.apiMapping?.label || 'name';
        const formLabelMap = childFieldConfig.apiMapping?.label_as || apiLabelMap;
        if (childFieldConfig.field !== formLabelMap) {
            const labelValue = Array.isArray(optionObj) ? optionObj.map(o => o.label) : optionObj.label;
            updatedItem = setNestedValue(updatedItem, formLabelMap, labelValue);
        }
    }

    // Script sửa được CẢ bản ghi mẹ (`master.tien_hang = …` — mẫu quen thuộc: đổi số lượng thì tính lại
    // tổng tiền trên master). `masterData` nằm ở state của form CHA nên sửa tại chỗ thì cha không vẽ
    // lại; chạy script trên một BẢN SAO rồi gửi phần chênh lệch cấp một lên cùng với mảng chi tiết.
    //
    // ⚠️ `master.<mảng chi tiết>` phải là mảng MỚI, đã có dòng đang sửa với giá trị mới. Bộ cũ ghi giá
    // trị vào chính phần tử nằm trong mảng RỒI mới chạy script (`InputBaseGridItem.js:277-289`), nên
    // `master.details` ở đó luôn là dữ liệu hiện tại. Đưa mảng cũ vào là mọi script kiểu
    // `master.t_so_luong = utils.sum(master.details,"sl_xuat")` LUÔN CHẬM MỘT NHỊP GÕ — sai âm thầm,
    // và chỉ đúng trở lại khi người dùng gõ thêm một lần nữa.
    let masterPatch = null;
    let finalArray = newArray;
    if (typeof childFieldConfig.onValueChanged === 'function') {
        const oldValue = getNestedValue(newArray[index], childFieldConfig.field);
        const arrayKey = field.field;
        const nextArray = [...newArray];
        nextArray[index] = updatedItem;
        // Mốc so sánh ĐÃ mang mảng mới -> `patch` chỉ chứa đúng những gì SCRIPT sửa.
        const baseline = arrayKey ? { ...(masterData || {}), [arrayKey]: nextArray } : { ...(masterData || {}) };
        const masterDraft = { ...baseline };
        const before = { ...updatedItem };
        try {
            let updates = childFieldConfig.onValueChanged(...buildValueChangedArgs({
                field: childFieldConfig.field, newValue, oldValue, optionObj,
                // `detail`/`data` của bộ cũ là chính DÒNG này — và nó cũng chính là phần tử trong
                // `master.<mảng>`, nên script sửa qua đường nào cũng ra cùng một object.
                formData: updatedItem, master: masterDraft, oldData: newArray[index],
                fields: subFields, context, utils, moment: Moment, numeral: Numeral,
            }));
            if (updates && typeof updates.then === 'function') updates = await updates;
            if (updates && typeof updates === 'object') updatedItem = { ...updatedItem, ...updates };
            const patch = masterPatchFrom(masterDraft, baseline);
            // Script thay CẢ mảng chi tiết thì mảng đó thắng; nó đi theo đường `onChange`, không đi kèm patch.
            if (arrayKey && Object.prototype.hasOwnProperty.call(patch, arrayKey)) {
                if (Array.isArray(patch[arrayKey])) finalArray = patch[arrayKey];
                delete patch[arrayKey];
            }
            if (Object.keys(patch).length) masterPatch = patch;
        } catch (e) {
            updatedItem = { ...updatedItem, ...before };
            console.error(`[ObjectArrayControl] script onValueChanged của field "${childFieldConfig.field}" lỗi:`, (e && e.message) || e);
        }
    }

    if (finalArray === newArray) newArray[index] = updatedItem;
    onChange(finalArray, masterPatch);
    if (cellModalData && cellModalData.idx === index && cellModalData.subF.field === childFieldConfig.field) {
        setCellModalData(prev => ({ ...prev, item: updatedItem }));
    }
  };

  const getRowSummary = (item, idx) => {
    const titleField = subFields.find(f => f.isTitle && !evaluateSubFieldCondition(f.hideInForm, item));
    const subTitleField = subFields.find(f => f.isSubTitle && !evaluateSubFieldCondition(f.hideInForm, item));
    let summaryText = "";
    if (titleField && getNestedValue(item, titleField.field)) summaryText += getNestedValue(item, titleField.field);
    if (subTitleField && getNestedValue(item, subTitleField.field)) summaryText += summaryText ? ` - ${getNestedValue(item, subTitleField.field)}` : getNestedValue(item, subTitleField.field);
    if (summaryText) return summaryText;
    const fallbackReq = subFields.find(f => f.type === 'String' && f.required && !evaluateSubFieldCondition(f.hideInForm, item) && getNestedValue(item, f.field));
    if (fallbackReq) return getNestedValue(item, fallbackReq.field);
    return `Dòng ${idx + 1}`;
  };

  // Các callback truyền xuống dòng: gói qua ref nên identity KHÔNG BAO GIỜ đổi, mà vẫn luôn gọi
  // đúng bản logic mới nhất (mẫu "latest callback").
  const handleChildChangeRef = useRef(handleChildChange); handleChildChangeRef.current = handleChildChange;
  const stableCellChange = useCallback((idx, subF, newVal, optionObj) => handleChildChangeRef.current(idx, subF, newVal, optionObj), []);

  const openCellModalRef = useRef(openCellModal); openCellModalRef.current = openCellModal;
  const stableOpenCellModal = useCallback((idx, subF, item) => openCellModalRef.current(idx, subF, item), []);

  // KHOÁ THEO TỪNG Ô của lưới chi tiết. `readOnly` của một cột được phép là script (đã được
  // parseDataListConfig biên dịch) nên phải xét theo TỪNG DÒNG. Dùng ref cho các giá trị đổi theo render
  // để callback ỔN ĐỊNH — dòng nào cần vẽ lại đã do `areRowPropsEqual` quyết định (`item` đổi, hoặc
  // `dependsOnMaster` khi script đọc dữ liệu chứng từ mẹ).
  const isCellReadOnly = useCallback((subF, rowData) => {
    const condition = subF && subF.readOnly;
    if (!condition) return false;
    if (typeof condition === 'function') {
      try { return !!condition(context, rowData, masterDataRef.current || rowData, subFieldsRef.current); }
      catch (e) { return false; }
    }
    // Chuỗi = script CHƯA biên dịch (cấu hình không qua parseDataListConfig). Coi như KHÔNG khoá:
    // `!!"return false"` là true, tức mọi cột có script sẽ hoá chỉ-đọc và không ai nhập được gì.
    if (typeof condition === 'string') return false;
    return !!condition;
  }, [context]);

  const isHiddenCell = useCallback((subF, rowData) => {
    const condition = subF.hideInForm;
    if (!condition) return false;
    if (typeof condition === 'function') {
      try { return condition(context, rowData, masterDataRef.current || rowData, subFieldsRef.current); }
      catch (e) { return false; }
    }
    return !!condition;
  }, [context]);

  const isLargeType = useCallback((subF) => LARGE_FIELD_TYPES.includes(subF.type), []);

  const rowErrorsOf = (idx) => {
    const out = {};
    for (const subF of subFields) {
      const msg = formErrors[`${field.field}_${idx}_${subF.field}`];
      if (msg) out[subF.field] = msg;
    }
    return out;
  };

  const ActionButtons = ({ idx, isTable = false }) => (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: isTable ? 'center' : 'flex-start' }} onClick={(e) => e.stopPropagation()}>
      <IconButton size="small" onClick={(e) => handleMoveRow(e, idx, 'up')} disabled={idx === 0} sx={{ color: 'action.active', '&.Mui-disabled': { opacity: 0.3 } }}>
        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton size="small" onClick={(e) => handleMoveRow(e, idx, 'down')} disabled={idx === subItems.length - 1} sx={{ color: 'action.active', '&.Mui-disabled': { opacity: 0.3 } }}>
        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton size="small" onClick={(e) => handleDuplicateRow(e, idx)} sx={{ color: 'success.main', bgcolor: (theme) => alpha(theme.palette.success.main, 0.1) }}>
        <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton size="small" color="error" onClick={(e) => handleRemoveRow(e, idx)} sx={{ bgcolor: (theme) => alpha(theme.palette.error.main, 0.1) }}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );

  // Bản ổn định để truyền xuống dòng đã memo (ActionButtons là component dựng lại mỗi render).
  const renderActionsRef = useRef(null);
  renderActionsRef.current = (idx) => <ActionButtons idx={idx} isTable={true} />;
  const stableRenderActions = useCallback((idx) => renderActionsRef.current(idx), []);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: '12px', border: '1px solid', borderColor: parentError && !parentError.includes('_') ? 'error.main' : 'divider', bgcolor: isReadOnly ? 'action.hover' : 'background.paper', overflow: 'hidden', ...(fillHeight ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}) }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
        {/* 👇 Áp dụng renderLabelWithHelp cho tiêu đề cha */}
        <Typography variant="body2" fontWeight="600" color={parentError && !parentError.includes('_') ? 'error.main' : 'text.primary'}>
          {renderLabelWithHelp(field.label, field.helpText, field.required)}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isPrimitiveArray && (
            <Button size="small" variant="outlined" color="inherit" startIcon={viewMode === 'table' ? <ViewListIcon /> : <TableChartIcon />} onClick={() => setManualViewMode(viewMode === 'table' ? 'list' : 'table')} sx={{ textTransform: 'none', borderRadius: 2, borderColor: 'divider' }}>
              {viewMode === 'table' ? 'Dạng danh sách' : 'Dạng bảng'}
            </Button>
          )}
          {!isReadOnly && <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAddRow} sx={{ textTransform: 'none', borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), color: 'primary.main', '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2) } }}>Thêm dòng</Button>}
        </Box>
      </Box>

      {parentError && !parentError.includes('_') && <Typography variant="caption" color="error.main" sx={{ mb: 1, display: 'block' }}>{parentError}</Typography>}

      {subItems.length === 0 ? (
         <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3, fontStyle: 'italic', bgcolor: 'background.default', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>Chưa có dữ liệu</Typography>
      ) : (
         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, ...(fillHeight ? { flexGrow: 1, minHeight: 0 } : {}) }}>
           {!isPrimitiveArray && viewMode === 'table' ? (
             // TableContainer cuộn CHỈ phần dòng; header table sticky (giữ cố định). fillHeight → lấp đầy chiều cao.
             <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default', maxHeight: fillHeight ? 'none' : '600px', overflow: 'auto', ...(fillHeight ? { flexGrow: 1, minHeight: 0 } : {}) }}>
               {/* 👇 THAY ĐỔI: Thêm stickyHeader */}
               <Table stickyHeader size="small" sx={{ minWidth: 600, borderCollapse: 'separate', borderSpacing: 0 }}>
                 <TableHead>
                   <TableRow>
                     {/* Cố định cột STT (#) Header - Cần zIndex cao nhất để nổi trên cả scroll dọc & ngang */}
                     <TableCell align="center" sx={{ width: 40, fontWeight: 'bold', border: '1px solid', borderColor: 'divider', bgcolor: '#f4f6f8', position: 'sticky', left: 0, zIndex: 3 }}>
                        #
                     </TableCell>
                     
                     {visibleTableColumns.map(subF => (
                       <TableCell key={subF.field} sx={{ fontWeight: 'bold', minWidth: subF.tableWidth || 150, border: '1px solid', borderColor: 'divider', bgcolor: '#f4f6f8', zIndex: 2 }}>
                         {renderLabelWithHelp(subF.label, subF.helpText, subF.required)}
                       </TableCell>
                     ))}
                     
                     {/* Cố định cột Thao tác Header */}
                     {!isReadOnly && (
                       <TableCell align="center" sx={{ width: 140, fontWeight: 'bold', border: '1px solid', borderColor: 'divider', bgcolor: '#f4f6f8', position: 'sticky', right: 0, zIndex: 3 }}>
                         Thao tác
                       </TableCell>
                     )}
                   </TableRow>
                 </TableHead>
                 <TableBody>
                   {subItems.map((item, idx) => (
                     <DetailTableRow
                       key={idx}
                       idx={idx}
                       item={item}
                       visibleColumns={visibleTableColumns}
                       subFields={subFields}
                       cellFields={cellFields}
                       isReadOnly={isReadOnly}
                       isActive={!isReadOnly && !!activeCell && activeCell.row === idx}
                       focusField={activeCell && activeCell.row === idx ? activeCell.field : null}
                       rowErrors={rowErrorsOf(idx)}
                       masterData={masterData}
                       dependsOnMaster={dependsOnMaster}
                       context={context}
                       isLast={idx === subItems.length - 1}
                       isHiddenCell={isHiddenCell}
                       isCellReadOnly={isCellReadOnly}
                       isLargeType={isLargeType}
                       onActivate={activateCell}
                       onCellChange={stableCellChange}
                       onOpenCellModal={stableOpenCellModal}
                       renderActions={stableRenderActions}
                     />
                   ))}
                 </TableBody>
               </Table>
             </TableContainer>
           ) : (
             subItems.map((item, idx) => {
                const isExpanded = !!expanded[idx];
                const hasChildError = subFields.some(subF => formErrors[`${field.field}_${idx}_${subF.field}`]);
                return (
                  <Box key={idx} sx={{ position: 'relative', borderRadius: 2, border: '1px solid', borderColor: hasChildError ? 'error.main' : 'divider', bgcolor: 'background.default', overflow: 'hidden' }}>
                    <Box onClick={() => toggleExpand(idx)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, cursor: 'pointer', bgcolor: isExpanded ? 'transparent' : 'rgba(0,0,0,0.02)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, pr: 2 }}>
                        {isExpanded ? <KeyboardArrowDownIcon fontSize="small" color="action" /> : <KeyboardArrowRightIcon fontSize="small" color="action" />}
                        <Typography variant="body2" fontWeight="500" color={hasChildError ? 'error.main' : 'text.primary'} sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{getRowSummary(item, idx)}</Typography>
                      </Box>
                      {!isReadOnly && <ActionButtons idx={idx} />}
                    </Box>
                    <Collapse in={isExpanded} unmountOnExit>
                      <Box sx={{ p: { xs: 1.5, sm: 2 }, pt: 0 }}>
                        <Grid container spacing={1.5}>
                            {subFields.map(subF => {
                              if (evaluateSubFieldCondition(subF.hideInForm, item)) return null;
                              return (
                                <Grid size={subF.gridEdit || { xs: 12 }} key={subF.field}>
                                    <BaseFieldControl 
                                      field={{ ...subF }}
                                      //label={renderLabelWithHelp(subF.label, subF.helpText, subF.required)}
                                      value={getNestedValue(item, subF.field)}
                                      onChange={(newVal, optionObj) => handleChildChange(idx, subF, newVal, optionObj)}
                                      error={formErrors[`${field.field}_${idx}_${subF.field}`]}
                                      isReadOnly={isReadOnly || isCellReadOnly(subF, item)} formData={item} masterData={masterData} formFields={subFields}
                                    />
                                </Grid>
                              )
                            })}
                        </Grid>
                      </Box>
                    </Collapse>
                  </Box>
                );
             })
           )}
         </Box>
      )}

      {/* ================= MODAL MỞ RỘNG ================= */}
      <Dialog open={!!cellModalData} onClose={closeCellModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, bgcolor: '#f8f9fa' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="h6" fontWeight="bold">
            {/* 👇 Hiển thị icon help ngay trong tiêu đề Modal */}
            {cellModalData && renderLabelWithHelp(cellModalData.subF.label, cellModalData.subF.helpText, false)} 
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              (Dòng {cellModalData ? cellModalData.idx + 1 : ''})
            </Typography>
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {cellModalData && (
            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <BaseFieldControl 
                field={{ ...cellModalData.subF, helpText: '' }} // Ẩn helpText vì đã hiện trên DialogTitle
                value={getNestedValue(cellModalData.item, cellModalData.subF.field)}
                onChange={(newVal, optionObj) => handleChildChange(cellModalData.idx, cellModalData.subF, newVal, optionObj)}
                isReadOnly={isReadOnly || isCellReadOnly(cellModalData.subF, cellModalData.item)}
                formData={cellModalData.item} masterData={masterData} formFields={subFields}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={closeCellModal} variant="contained" disableElevation>Đóng / Thu gọn</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}